// Mirrors QuotationStatus enum (backend: com.kervolt.oms.enums.QuotationStatus)
export type QuotationStatus = 'DRAFT' | 'SENT' | 'CONFIRMED' | 'REJECTED' | 'EXPIRED' | 'CONVERTED';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export const QUOTATION_STATUS_COLORS: Record<QuotationStatus, ChipColor> = {
  DRAFT: 'default',
  SENT: 'info',
  CONFIRMED: 'primary',
  REJECTED: 'error',
  EXPIRED: 'warning',
  CONVERTED: 'success',
};

// Mirrors QuotationLineDTO (IdHolder gives id)
export interface QuotationLine {
  id: string;
  quotationId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRatePct: number;
  lineTax?: number;
  lineTotal?: number;
  revenueAccountId?: string;
  segmentId?: string;
  taxCodeId?: string | null;
  // ---- FW-2A: line discount (gross = quantity x unit price; lineTotal is the NET after the discount) and catalogue item ----
  grossAmount?: number | null;
  discountPct?: number | null;
  discountAmount?: number | null;
  itemId?: string | null;
  // ---- end FW-2A ----
  /** G5b: on a foreign quotation, the entered unit price and the line net in the quotation currency. */
  foreignUnitPrice?: number | null;
  foreignAmount?: number | null;
}

// Mirrors QuotationDTO (IdHolder gives id)
export interface Quotation {
  id: string;
  salespersonId?: string | null;   // S0
  salespersonName?: string | null;
  salesTeamId?: string | null;
  quotationNumber?: string;
  customerId: string;
  customerName?: string;
  quotationDate: string;
  validUntil: string;
  status: QuotationStatus;
  confirmedById?: string;
  confirmedAt?: string;
  rejectReason?: string;
  convertedInvoiceId?: string;
  convertedSalesOrderId?: string;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  notes?: string;
  clonedFromId?: string;
  institutionId: string;
  lines: QuotationLine[];
  // ---- G5b: document currency (BDT totals stay the ledger amounts at the indicative rate) ----
  currencyCode?: string;
  exchangeRate?: number | null;
  foreignSubtotal?: number | null;
  foreignTotal?: number | null;
  // ---- S1: discount approval (the send waits for the quote's team manager or department head) ----
  approvalStatus?: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';
  approvalNote?: string | null;
  /** Highest line discount % of a draft. */
  maxDiscountPct?: number | null;
  /** True when sending this draft needs an approval first. */
  approvalRequired?: boolean | null;
  // ---- end S1 ----
  /** Soft warnings of the last save (rate far from the table, export overlap); never blocking. */
  warnings?: string[];
}

// Mirrors QuotationLineCreateDTO
export interface QuotationLineCreateDTO {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRatePct: number;
  revenueAccountId?: string;
  segmentId?: string;
  taxCodeId?: string | null;
  // ---- FW-2A: line discount (gross = quantity x unit price; lineTotal is the NET after the discount) and catalogue item ----
  discountPct?: number | null;
  discountAmount?: number | null;
  itemId?: string | null;
  // ---- end FW-2A ----
}

// Mirrors QuotationCreateDTO
export interface QuotationCreateDTO {
  /** S0: owner; omit for the customer's salesperson, else you. Naming someone else needs SALES_REASSIGN. */
  salespersonId?: string | null;
  customerId: string;
  quotationDate: string;
  validUntil?: string;
  notes?: string;
  institutionId: string;
  lines: QuotationLineCreateDTO[];
  /** G5b: omit (or BDT) for a BDT quotation; on a foreign one unit prices and amount discounts are in this currency. */
  currencyCode?: string;
  exchangeRate?: number | null;
}

// Mirrors QuotationRejectDTO — field name is "reason" (not rejectReason)
export interface QuotationRejectDTO {
  reason: string;
}

export interface QuotationPage {
  content: Quotation[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}
